import type { ProwlPageContext } from './contracts';

/**
 * Best-effort `entityId` extraction for known customer-portal routes. This is
 * a small, explicit table rather than a generic parser — wrong guesses are
 * silently ignored by the agent (design-spec §7's pageContext is advisory
 * context, not a validated argument), so a conservative table beats a clever
 * regex that occasionally captures the wrong path segment.
 */
const ENTITY_ID_PATTERNS: { pattern: RegExp; group: number }[] = [
  // /services/service-details/:displayId/:slug
  { pattern: /^\/services\/service-details\/([^/]+)/, group: 1 },
  // /customer/bookings/:bookingId
  { pattern: /^\/customer\/bookings\/([^/]+)/, group: 1 },
];

const extractEntityId = (pathname: string): string | undefined => {
  for (const { pattern, group } of ENTITY_ID_PATTERNS) {
    const match = pathname.match(pattern);
    if (match?.[group]) return match[group];
  }
  return undefined;
};

/**
 * Builds `{ route, entityId }` (task requirement 8) from the current
 * location at call time — read fresh on every `send()` rather than kept in
 * state, so it's always accurate even though the shared Prowl session
 * outlives any single route (ProwlSessionContext.tsx).
 */
export const getProwlPageContext = (): ProwlPageContext => {
  const pathname = window.location.pathname;
  return {
    route: pathname,
    entityId: extractEntityId(pathname),
  };
};
